import { configurationPartSeed } from '@misumi/shared'
import { useMemo } from 'react'
import { PartConfiguratorShell } from '../parts/components/PartConfiguratorShell'
import type {
  PartConfiguratorFormState,
  PartConfiguratorSaveSeam,
} from '../parts/components/part-configurator-save'
import type { RfqLineSpecification } from './configuration-estimate'
import type { RfqReviewLine } from './review-domain'
import { RfqLineConfiguratorPricingFooter } from './rfq-configurator-pricing-footer'
import { RFQ_LINE_CONFIGURATOR_CAPABILITIES, fromRfqReviewLine } from './rfq-configurator-subject'

export interface RfqLineConfiguratorModalProps {
  readonly open: boolean
  readonly onClose: () => void
  /** The one line being configured — the per-row Configure button and the
   * Requirements-cell pencil (`PartsReviewTable.tsx`) both mount this SAME
   * component; the bulk toolbar's "Configure N" stays on `ConfigurePartsModal`
   * unchanged (UID-434/WS-D splits the two funnels apart — see this
   * component's own report). */
  readonly line: RfqReviewLine
  /** Called with this line's id, the collected `RfqLineSpecification`, and
   * the shell's current quantity value — the SAME
   * `POST /rfq/{rfqId}/lines/bulk-configure` seam `ConfigurePartsModal`'s
   * `onSave` already targets (`RfqReviewPage.handleSaveConfigure`), just
   * invoked with a single-element id list. This adapter has no `rfqId` and
   * no draft-sync opinion of its own — same posture as
   * `ConfigurePartsModal`. */
  readonly onSave: (
    lineId: string,
    specification: RfqLineSpecification,
    quantity: number,
  ) => Promise<{ readonly ok: true } | { readonly ok: false; readonly message: string }>
  /** UID-434/WS-D follow-up — "Configure next part" (dc.html:5700-5704/
   * 5718-5720). Forwarded to `PartConfiguratorShell` unchanged: `onAdvance`
   * is invoked only after the shell has persisted the CURRENT line via
   * `onSave` above. `RfqReviewPage` supplies this as "re-target this same
   * open modal at the next needs-config line" — never a close+reopen.
   * Absent (e.g. this modal used outside an active RFQ draft's per-line
   * flow, or no OTHER line needs Configure) means the CTA never renders,
   * the same optional-callback convention `onSave`'s siblings on this modal
   * already use.
   *
   * Gate 5 Suggestion 10 — see `PartConfiguratorShellProps.nextPart`'s own
   * doc comment for why this collapses the former separate `onNextPart`/
   * `hasNextPart` pair into one object.
   *
   * Gate 5 C3 (structural fix) contract — `onAdvance` MUST result in a
   * changed `subject`/`initial` reference on `PartConfiguratorShell`'s next
   * commit (in practice: a changed `subject.id`, i.e. the `RfqReviewPage`
   * caller MUST re-target `configureLineId` to a different line). The shell
   * holds `saving` true until its own reset effect observes that new
   * reference; a caller that fails to re-target leaves the shell's actions
   * permanently disabled. See `PartConfiguratorShellProps.nextPart`'s doc
   * comment for the full mechanism. */
  readonly nextPart?: { readonly onAdvance: () => void } | undefined
}

/** The shell's generic `PartConfiguratorFormState` mapped onto
 * `RfqLineSpecification` — field-for-field the SAME mapping
 * `rfq-configurator-pricing-footer.tsx`'s read-only estimate uses, kept as
 * one function here (the save path) that module imports... actually kept
 * separate per-file (both small, no runtime state to share) but structurally
 * identical so the two can never drift on what a given form state means. */
function formToRfqLineSpecification(form: PartConfiguratorFormState): RfqLineSpecification {
  return {
    process: form.process,
    // Guarded by the caller (`save` below) — `material` is only read here
    // once it has already been confirmed non-null/non-empty.
    material: form.material ?? '',
    finish: form.finish,
    ...(form.inspections && form.inspections.length > 0
      ? { inspections: [...form.inspections] }
      : {}),
    ...(form.certificates && form.certificates.length > 0
      ? { certificates: [...form.certificates] }
      : {}),
    // ADR-0117 D4.1 — RFQ-only routing state; `EditPartConfigRequest` (Part
    // Library's own form-to-request mapper, `EditConfigurationModal.tsx`)
    // has no field to put this on, so only THIS mapper ever reads it.
    ...(form.customInspection ? { customInspection: true } : {}),
  }
}

/**
 * UID-434/WS-D — the RFQ per-line entry point onto the shared
 * `PartConfiguratorShell`, the RFQ sibling of `EditConfigurationModal.tsx`
 * (Part Library's own thin adapter). Supplies:
 *  - the `fromRfqReviewLine` subject adapter,
 *  - RFQ's own capability set (`RFQ_LINE_CONFIGURATOR_CAPABILITIES` — no DFM,
 *    no attachments, no "Apply to multiple parts", and the field-set
 *    asymmetry gating: no color/threaded-holes/additional-requirements
 *    controls, plus the RFQ-only custom-inspection option),
 *  - RFQ's own LIVE, non-mutating pricing/footer seam
 *    (`RfqLineConfiguratorPricingFooter`, backed by
 *    `useConfigurationEstimate` — never `subject.indicativePrice`), and
 *  - RFQ's own save seam, wrapping the SAME
 *    `POST /rfq/{rfqId}/lines/bulk-configure` call the bulk entry point uses,
 *    invoked with this one line's id.
 *
 * Per ADR-0117 D9.1 and this ticket's human D4 ruling, none of these four
 * seams is unified with Part Library's/Cart's own — each entry point keeps
 * its own.
 */
export function RfqLineConfiguratorModal({
  open,
  onClose,
  line,
  onSave,
  nextPart,
}: RfqLineConfiguratorModalProps) {
  // Mirrors `EditConfigurationModal.tsx`'s own `subject` memo — depends on
  // `line` alone so a successful "Save this configuration" (which does not
  // close the modal) never resets the shell's in-progress field state via a
  // parent re-render that hands back the SAME line reference.
  //
  // Gate 5 C2 — that premise ("the SAME line reference") only holds because
  // `RfqReviewPage`'s own `lines` is now memoized off its three real inputs
  // (`rfqBundle.items`/`quantityOverrides`/`specSelections`, see that file's
  // comment on its `lines` declaration). Before that fix, `lines` was a
  // fresh `.map()` on EVERY render of that page, so `line` here was a fresh
  // object on every render too — this memo's `[line]` dependency changed on
  // every render right along with it, defeating the whole point: any
  // unrelated re-render of the page (a sibling quantity change, a toast
  // timer, `handleSaveConfigure`'s own post-save `setDraft` + `showToast`)
  // reset every in-progress field via the shell's `[open, initial]` effect.
  // This memo alone was never enough to guarantee that; fix it at the
  // source (`RfqReviewPage.tsx`) if this stops holding again.
  const subject = useMemo(() => fromRfqReviewLine(line), [line])

  /**
   * UID-604 / ADR-0177 §4b — this line's DEMO-ONLY per-part price seed, for the
   * footer's live estimate. Resolved through the SHARED `configurationPartSeed`
   * ladder so the footer, `RfqService.buildSpecSelection` and the auto-quote
   * runner provably use ONE rule; if they disagreed, the footer would show one
   * price and the saved RFQ another.
   *
   * READ OFF `line.sourceable`, NOT `line.name`, and that is the load-bearing
   * detail. `RfqReviewLine.name` is `sourceable.name ?? 'Item <first 8 chars of
   * id>'` (`review-domain.ts`) — for a nameless part it is a string the SERVER
   * HAS NEVER SEEN, so seeding from it would guarantee disagreement exactly on
   * the lines where identity is weakest. `sourceable.name` / `sourceable
   * .catalogSku` are the raw, un-synthesized strings, and they are the very two
   * the server's own snapshot is built from
   * (`part-requirements-lookup.ts` resolves `part.sourceable.name` and
   * `part.sourceable.catalogSku` into the `linePartIdentity` row that
   * `buildSpecSelection` then seeds off). Same two strings, same ladder, so
   * agreement is structural rather than hoped-for — and when neither is present
   * BOTH sides send nothing and both price at the un-seeded base, so agreement
   * holds in the degenerate case too.
   *
   * WHERE THE IDENTITY COMES FROM (UID-605, ADR-0177 Amendment A1). This modal is
   * reached ONLY from `RfqReviewPage`, whose `lines` are built from the CLIENT
   * DRAFT BUNDLE (`rfqBundle.items`) and never from the server read wire — so
   * `RfqLineView.part`, which §4.2 originally named as the client's source for
   * this seed, is not in scope here and never was. §4 is restated over the
   * identity the client ACTUALLY holds: `ReviewLineSourceable`'s raw
   * `name`/`catalogSku`/`customerBomId`, which are the very columns the server's
   * `linePartIdentity` snapshot is built from. Both sides then normalize through
   * the SAME shared function (Amendment A4 moved the trim, the 255/128 caps and
   * the third rung INTO it), so agreement holds by construction rather than by
   * two adapters coincidentally matching.
   *
   * An earlier version of this comment claimed the compose screen was the
   * degenerate case §4.3 sanctions, and that closing the gap needed a
   * `PersistedRfqBundleItem` schema-version bump. BOTH WERE WRONG. It was not
   * symmetric absence but ASYMMETRIC availability — the client lacked identity
   * while the server had it, which is the disagreement §4 exists to prevent, not
   * an instance of it holding. And no bump was needed: the fields already survive
   * the rest-spread persist, and ADR-0171's own criterion does not cover an
   * optional field with a rendered-for-absence path (Amendment A3).
   *
   * WHAT REMAINS UN-SEEDABLE (Amendment A5) — a real residue, not a gap to close:
   * a catalog-SEARCH part (id freshly minted per result card, absent from
   * `library.part`, so the SERVER holds nothing while the client holds a name), a
   * part with neither name nor SKU nor BOM id, and the server's fail-open
   * library-read degradation. For those this resolves to `undefined`, both sides
   * price at the un-seeded base, and the footer SAYS SO — see
   * `PartConfiguratorPricingFooter`'s caveat on `partSeed === undefined`.
   *
   * The caps are no longer an edge: `configurationPartSeed` applies the same
   * 255/128 bounds the server's snapshot does, and is idempotent against
   * already-bounded input.
   */
  const partSeed = useMemo(
    () =>
      configurationPartSeed({
        name: line.sourceable.name,
        sku: line.sourceable.catalogSku,
        // The third rung, matching the server's `boundedOrNull(catalogSku) ??
        // boundedOrNull(customerBomId)` collapse. A BOM-sourced part with a null
        // library name and a real BOM id used to seed on the server and NOT here.
        bomId: line.sourceable.customerBomId,
      }),
    // The three strings, not the whole `line`: an unrelated field changing (a
    // quantity edit, a spec selection) must not re-derive the seed and re-fire
    // the estimate request.
    [line.sourceable.name, line.sourceable.catalogSku, line.sourceable.customerBomId],
  )

  const saveSeam: PartConfiguratorSaveSeam = useMemo(
    () => ({
      async save(form: PartConfiguratorFormState) {
        // UID-434/WS-D — the shell itself does not gate Save on
        // material/finish being chosen (unlike `ConfigurePartsModal`'s
        // `cascade.ready`); `RfqLineSpecification.material` is REQUIRED and
        // non-empty, so an unconfigured material must be caught HERE rather
        // than sent to the server as an empty string (a 400 the buyer would
        // see as an unexplained save failure). Surfaced as a field error on
        // the Material section, matching the shell's own field-error
        // rendering.
        if (!form.material) {
          return {
            ok: false as const,
            kind: 'field_errors' as const,
            errors: [
              { field: 'material', detail: 'Select a material to save this configuration.' },
            ],
          }
        }

        const specification = formToRfqLineSpecification(form)
        const result = await onSave(line.id, specification, form.quantity)
        if (!result.ok) {
          return { ok: false as const, kind: 'error' as const, message: result.message }
        }

        // The server is the source of truth for the resulting
        // `specSelection` (indicativeUnitPrice/pricingOutcome are
        // server-derived, ADR-0117 D7) — `RfqReviewPage` merges the REAL
        // returned selection into its own `specSelections` state and this
        // component re-renders with a fresh `line` prop reflecting it. This
        // refreshed subject is therefore an OPTIMISTIC projection of exactly
        // what was just submitted, only so the shell's post-save
        // "Manufacturability feedback" tab switch has something coherent to
        // show in the interim — never a fabricated price.
        return {
          ok: true as const,
          subject: fromRfqReviewLine({
            ...line,
            quantity: form.quantity,
            specSelection: { specification },
          }),
        }
      },
    }),
    [line, onSave],
  )

  return (
    <PartConfiguratorShell
      open={open}
      onClose={onClose}
      subject={subject}
      capabilities={RFQ_LINE_CONFIGURATOR_CAPABILITIES}
      saveSeam={saveSeam}
      pricingFooter={RfqLineConfiguratorPricingFooter}
      nextPart={nextPart}
      partSeed={partSeed}
    />
  )
}
